import { translate, useLanguage } from "@cqt/platform";
/**
 * Who else is here.
 *
 * The list comes from `GET /directory`, which is scoped SERVER-SIDE to the
 * caller's active company by `app/people.py`. This component does no filtering
 * of its own and must never start: on the platform being replaced, the People
 * tab showed one company's staff to another because three screens each wrote
 * their own version of that query.
 */
import { useQuery } from "@tanstack/react-query";
import { ErrorBox, Loading, useApi } from "@cqt/platform";
import { Avatar, displayName } from "./Avatar";

interface Person {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  title: string | null;
  avatar_url: string | null;
  is_superadmin: boolean;
}

export function PeopleTab({ companyName }: { companyName: string }) {
  useLanguage();
  const api = useApi();
  const people = useQuery({
    queryKey: ["directory"],
    queryFn: () => api.get<Person[]>("/directory"),
    staleTime: 60_000,
  });

  if (people.isPending) return <Loading label={translate("Loading people")} />;
  if (people.error) return <ErrorBox error={people.error} />;
  const rows = people.data ?? [];

  return (
    <div className="card">
      <div className="card__head">
        <div>
          <h3>{translate("People")}</h3>
          <p className="meta">
             {translate("Everyone with access to")} {companyName}{translate(". Only this company — someone who works for two of them appears in each separately.")} </p>
        </div>
        <span className="chip">{rows.length}</span>
      </div>
      <div className="card__body people">
        {rows.map((person) => (
          <div className="person" key={person.id}>
            <Avatar person={person} />
            <div className="person__meta">
              <div className="person__name">
                {displayName(person)}
                {person.username ? <span className="person__handle">@{person.username}</span> : null}
                {person.is_superadmin ? <span className="chip chip--accent">{translate("Admin")}</span> : null}
              </div>
              <div className="meta">
                {person.title ? `${person.title} · ` : ""}
                <a href={`mailto:${person.email}`}>{person.email}</a>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 ? (
          // Reachable only if the caller is not themselves a member — the
          // directory always includes them otherwise.
          <p className="muted">{translate("Nobody else here yet.")}</p>
        ) : null}
      </div>
    </div>
  );
}
